/**
 * ShowroomListHeader Component
 *
 * A reusable header component for the showroom listing page.
 * Displays a hero banner with title and country links with counts.
 *
 * @component
 * @example
 * <ShowroomListHeader
 *   heroData={globalConfig?.showroomHero}
 *   countries={globalConfig?.countriesCollection?.items}
 *   isLoading={isLoadingGlobalConfig}
 * />
 */

import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';

import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils';
import {
  Box,
  Heading,
  HStack,
  Link,
  Skeleton,
  Text,
  useMultiStyleConfig,
  Wrap,
  WrapItem,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {getContainerStyles} from '@amblique/king-living/app/theme/components/project/showroom-list-header';

/**
 * Country Link component with count badge
 */
const CountryLink = ({country, isActive, styles}) => {
  const {linkText: text, url, totalCount, openInNewTab} = country;

  return (
    <Link
      href={url || '#'}
      rel={openInNewTab ? 'noopener noreferrer' : undefined}
      {...styles.countryLink}
      target={openInNewTab ? '_blank' : undefined}
    >
      {text}
      {totalCount != null && (
        <Text as="sup" {...styles.countBadge}>
          {totalCount}
        </Text>
      )}
    </Link>
  );
};

CountryLink.propTypes = {
  country: PropTypes.shape({
    sys: PropTypes.shape({
      id: PropTypes.string,
    }),
    linkText: PropTypes.string,
    url: PropTypes.string,
    totalCount: PropTypes.number,
    openInNewTab: PropTypes.bool,
  }).isRequired,
  isActive: PropTypes.bool,
  styles: PropTypes.object.isRequired,
};

/**
 * ShowroomListHeader Component
 *
 * @param {Object} props
 * @param {Object} props.heroData - HeroBanner data from Contentful (showroomHero)
 * @param {Array} props.countries - Array of country links with counts
 * @param {boolean} props.isLoading - Loading state
 * @param {string} props.currentSiteId - Current site ID to highlight active country
 */
export const ShowroomListHeader = ({
  heroData,
  countries,
  isLoading = false,
  currentSiteId,
  ...props
}) => {
  const {formatMessage} = useIntl();
  const styles = useMultiStyleConfig('ShowroomListHeader', {});

  const fallbackImage = getAssetUrl('static/img/showroom-header-bg.jpg');
  const {mobileAsset, tabletAsset, desktopAsset} = heroData?.backgroundMedia || {};

  const hasHeroBackgroundImage = Boolean(
    mobileAsset?.url || tabletAsset?.url || desktopAsset?.url
  );

  const backgroundImageByBreakpoint = useMemo(() => {
    if (!hasHeroBackgroundImage) {
      return {base: fallbackImage, md: fallbackImage, lg: fallbackImage};
    }

    return {
      base: mobileAsset?.url || tabletAsset?.url || desktopAsset?.url || fallbackImage,
      md: tabletAsset?.url || desktopAsset?.url || mobileAsset?.url || fallbackImage,
      lg: desktopAsset?.url || tabletAsset?.url || mobileAsset?.url || fallbackImage,
    };
  }, [hasHeroBackgroundImage, mobileAsset, tabletAsset, desktopAsset, fallbackImage]);

  // Get title from hero data or use translated fallback
  const title = heroData?.title || formatMessage({
    defaultMessage: 'Visit a King showroom',
    id: 'showroom_list_header.fallback_title',
  });

  const containerStyles = useMemo(() => {
    const toBackgroundUrl = (url) => (url ? `url('${url}')` : undefined);

    return {
      ...getContainerStyles(),
      sx: {
        backgroundImage: {
          base: toBackgroundUrl(backgroundImageByBreakpoint.base),
          md: toBackgroundUrl(backgroundImageByBreakpoint.md),
          lg: toBackgroundUrl(backgroundImageByBreakpoint.lg),
        },
      },
    };
  }, [backgroundImageByBreakpoint]);

  // Loading state
  if (isLoading) {
    return (
      <Box className="showroom-list-header" {...containerStyles} {...props}>
        <Skeleton height="300px" width="100%" />
      </Box>
    );
  }

  return (
    <Box className="showroom-list-header" {...containerStyles} {...props}>
      <Box {...styles.overlay} />

      <Box className="showroom-list-header-content-wrapper" {...styles.contentWrapper}>
        {/* Title - Bottom Left */}
        <Heading as="h1" {...styles.title}>
          {title}
        </Heading>

        {/* Country Links - Bottom Left, below title */}
        {countries && countries.length > 0 && (
          <HStack {...styles.countryLinksContainer}>
            <Wrap justify="flex-start" spacing={{base: '8px', md: '24px'}}>
              {countries.map((country) => (
                <WrapItem key={country.sys?.id || country.text}>
                  <CountryLink
                    country={country}
                    isActive={country.url?.includes(currentSiteId)}
                    styles={styles}
                  />
                </WrapItem>
              ))}
            </Wrap>
          </HStack>
        )}
      </Box>
    </Box>
  );
};

ShowroomListHeader.propTypes = {
  /**
   * HeroBanner data from Contentful (showroomHero field of GlobalConfig)
   */
  heroData: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    backgroundMedia: PropTypes.shape({
      desktopAsset: PropTypes.shape({
        url: PropTypes.string,
      }),
      mobileAsset: PropTypes.shape({
        url: PropTypes.string,
      }),
      tabletAsset: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
  }),

  /**
   * Array of country links from GlobalConfig.countriesCollection
   */
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      sys: PropTypes.shape({
        id: PropTypes.string,
      }),
      text: PropTypes.string,
      url: PropTypes.string,
      totalCount: PropTypes.number,
      openInNewTab: PropTypes.bool,
    })
  ),

  /**
   * Loading state - shows skeleton when true
   */
  isLoading: PropTypes.bool,

  /**
   * Current site ID to highlight active country link
   */
  currentSiteId: PropTypes.string,
};

export default ShowroomListHeader;
